class CacheService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
  }

  // Generate cache key from URL and params
  generateKey(url, params = {}) {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${url}${paramString ? '?' + paramString : ''}`;
  }

  // Check if cache entry is valid
  isValid(entry) {
    return entry && Date.now() - entry.timestamp < entry.ttl;
  }

  // Get cached data
  get(key) {
    const entry = this.cache.get(key);
    if (this.isValid(entry)) {
      return entry.data;
    }
    
    if (entry) {
      this.cache.delete(key);
    }
    
    return null;
  }

  // Set cached data
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  // Clear cache entry
  delete(key) {
    this.cache.delete(key);
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Prevent duplicate requests
  async deduplicate(key, requestFn) {
    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return await this.pendingRequests.get(key);
    }

    // Create new request
    const requestPromise = requestFn();
    this.pendingRequests.set(key, requestPromise);

    try {
      const result = await requestPromise;
      this.pendingRequests.delete(key);
      return result;
    } catch (error) {
      this.pendingRequests.delete(key);
      throw error;
    }
  }

  // Get cache stats
  getStats() {
    const entries = Array.from(this.cache.entries());
    const validEntries = entries.filter(([, entry]) => this.isValid(entry));
    
    return {
      total: entries.length,
      valid: validEntries.length,
      expired: entries.length - validEntries.length,
      pending: this.pendingRequests.size
    };
  }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;
